/**
 * <hidden-review-marquee> — reviews rail (gate 2026-07-29).
 * Desktop (≥1024): continuous marquee; pauses while the section area is hovered,
 * resumes on leave; prev/next arrows smoothly jump one card (skip-the-wait) and
 * the marquee resumes from the new position. Slides are cloned once
 * (aria-hidden) for a seamless wrap.
 * Mobile (<1024): no marquee — scroll-snap swipe + the same arrows.
 * Static under prefers-reduced-motion (arrows/swipe still work) and in the editor.
 */
class HiddenReviewMarquee extends HTMLElement {
  #raf = 0;

  #hoverPaused = false;

  /** performance.now() until which the marquee yields to an arrow jump */
  #jumpPausedUntil = 0;

  get #paused() {
    return this.#hoverPaused || performance.now() < this.#jumpPausedUntil;
  }

  #cloned = false;

  #originalWidth = 0;

  /** @type {MediaQueryList | null} */
  #desktop = null;

  /** @type {HTMLElement | null} */
  #track = null;

  #speed = 40; // px per second

  #lastTime = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-marquee-track]');
    if (!this.#track) return;

    for (const [selector, delta] of [
      ['[data-marquee-prev]', -1],
      ['[data-marquee-next]', 1],
    ]) {
      this.querySelector(String(selector))?.addEventListener('click', () => this.#jump(Number(delta)));
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || window.Shopify?.designMode) return;

    // Pause only while a CARD is hovered — not the whole section area
    // (dev refinement 2026-07-29); delegation instead of enter/leave on host.
    this.addEventListener('pointerover', (event) => {
      if (event.target instanceof Element && event.target.closest('.hidden-review-slide')) {
        this.#hoverPaused = true;
      }
    });
    this.addEventListener('pointerout', (event) => {
      const from = event.target instanceof Element && event.target.closest('.hidden-review-slide');
      const to = event.relatedTarget instanceof Element && event.relatedTarget.closest('.hidden-review-slide');
      if (from && !to) this.#hoverPaused = false;
    });

    this.#desktop = window.matchMedia('(min-width: 1024px)');
    const apply = () => (this.#desktop?.matches ? this.#start() : this.#stop());
    this.#desktop.addEventListener('change', apply);
    apply();
  }

  disconnectedCallback() {
    this.#stop();
  }

  #start() {
    if (!this.#track) return;
    if (!this.#cloned) {
      this.#originalWidth = this.#track.scrollWidth;
      for (const child of [...this.#track.children]) {
        const clone = child.cloneNode(true);
        if (clone instanceof HTMLElement) {
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-marquee-clone', '');
          this.#track.append(clone);
        }
      }
      this.#cloned = true;
    }
    this.#lastTime = 0;
    const step = (time) => {
      this.#raf = requestAnimationFrame(step);
      if (this.#paused || !this.#track) {
        this.#lastTime = time;
        return;
      }
      if (this.#lastTime) {
        this.#track.scrollLeft += (this.#speed * (time - this.#lastTime)) / 1000;
        if (this.#track.scrollLeft >= this.#originalWidth) {
          this.#track.scrollLeft -= this.#originalWidth;
        }
      }
      this.#lastTime = time;
    };
    this.#raf = requestAnimationFrame(step);
  }

  #stop() {
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  }

  /** @param {number} direction */
  #jump(direction) {
    if (!this.#track) return;
    // Yield the marquee while the smooth jump animates — otherwise its
    // per-frame scrollLeft writes stomp the smooth scroll (desktop bug fix).
    this.#jumpPausedUntil = performance.now() + 700;
    this.#lastTime = 0;
    const card = this.#track.querySelector(':scope > *');
    const width = card instanceof HTMLElement ? card.offsetWidth + 16 : this.#track.clientWidth / 2;
    this.#track.scrollBy({ left: width * direction, behavior: 'smooth' });
  }
}

if (!customElements.get('hidden-review-marquee')) {
  customElements.define('hidden-review-marquee', HiddenReviewMarquee);
}
